import { reactive, ref } from 'vue'
import { CAMERA_TYPES, cameraTypes, DroneSubEnumValue, PAYLOAD_WIDE_IR, PayloadSubEnumValue2 } from '../types/device'
import store from '../store'
import { message } from 'ant-design-vue'
import { BooleanEnum, BtnCallBackLoading, BtnLoading, FOCUS_MODE_TYPE, FollowBadType, FollowType, METERING_MODE_TYPE, RETURN_MODE_TYPE, ROUTE_TYPE, SCANNING_MODE_TYPE, WAYLINE_MAP_TYPE_NUM, WaylineSmoothType } from '../types/wayline'
import { state } from '../hooks/Cesium'
import { Cartesian3 } from 'cesium'
import { getWaylineStrip } from '../api/algCode'
import { calculateCesiumPolygonArea, calculateCesiumPolylineLength } from './coordinate'
import { GetNewPoint, GetPoint } from './kml'
import { CoordinateMode } from './kmlUtil'
import { createStartActions, getPointWpml } from './kmlPlanar'
import { getPointPosition, isSave } from './kmlParse'

export const pauseMapKeyCodeEvt = ref(false)

// 类型定义
interface RouteData {
  leftExpandDis: number;
  rightExpandDis: number;
  allExpandDis: number;
  acquisitionType: string;
  imageFormat: string[];
  shootType: string;
  stripRouteType: number;
  autoFlightSpeed: number;
  heightMode: string;
  globalHeight: number;
  minGlobalHeight: number;
  GSD: number;
  irGSD: number;
  minGSD: number;
  minIrGSD: number;
  orthoLidarOverlapW: number;
  finishAction: string;
  flyToWaylineMode: string;
  takeOffSecurityHeight: number;
  routeDistance: number;
  centerLineDistance: number;
  duration: number;
  area: number;
  isUnite: boolean;
}

// 响应式数据
export const currentStripRouteSettingData = reactive<RouteData>({
  acquisitionType: '',
  leftExpandDis: 50,
  rightExpandDis: 50,
  allExpandDis: 50,
  imageFormat: ['wide', 'zoom'],
  shootType: 'time',
  stripRouteType: 1,
  autoFlightSpeed: 10,
  heightMode: 'EGM96',
  globalHeight: 100,
  minGlobalHeight: 100,
  GSD: 5,
  irGSD: 5,
  minGSD: 5,
  minIrGSD: 5,
  orthoLidarOverlapW: 70,
  finishAction: 'goHome',
  flyToWaylineMode: 'safely',
  takeOffSecurityHeight: 20,
  routeDistance: 0,
  centerLineDistance: 0,
  duration: 0,
  area: 0,
  isUnite: true,
})
export const stripPoints = ref([])
export const stripArea = ref([])
export const stripAllRoutes = ref([])
// ---------- 纯函数工具 ----------
/**
 * 根据外扩距离计算 GSD（地面采样距离）
 * @param expandDistance 外扩距离（米）
 * @param payloadKey 载荷设备标识
 * @returns GSD 值与对应的红外 GSD
 */
function calculateGSDByExpandDistance (expandDistance: number, payloadKey: string) {
  const payload = PAYLOAD_WIDE_IR[payloadKey]
  if (!payload) return { GSD: 0, irGSD: 0 }

  const wideGSD = computeMr(payload.wideW, payload.wideF, payload.wideS, expandDistance)
  const irGSD = payload.irS ? computeMr(payload.irW, payload.irF, payload.irS, expandDistance) : 0

  return {
    GSD: Number(wideGSD.toFixed(1)),
    irGSD: Number(irGSD.toFixed(1)),
  }
}

/**
 * 计算 Mr 值（内部公式）
 */
function computeMr (FollowType: number, x: number, l: number, m: number): number {
  const angle = 2 * Math.atan(FollowType / (2 * x))
  return (m / Math.tan(angle / 2)) * (l / x) / 10
}

/**
 * 根据 GSD 计算飞行高度
 * @param gsd 目标 GSD（厘米/像素）
 * @param payloadKey 载荷标识
 * @param cameraType 相机类型（wide / ir）
 * @returns 高度（米）
 */
function getFlightHeightByGSD (gsd: number, payloadKey: string, cameraType: string): number {
  const payload = PAYLOAD_WIDE_IR[payloadKey]
  let sensorSize, focalLength
  if (cameraType === cameraTypes.IR) {
    sensorSize = payload.irS * 1e-4
    focalLength = payload.irF * 0.1
  } else {
    sensorSize = payload.wideS * 1e-4
    focalLength = payload.wideF * 0.1
  }
  const height = gsd * focalLength / (sensorSize * 100)
  return Number(height.toFixed(1))
}

/**
 * 根据飞行高度计算 GSD
 * @param height 飞行高度（米）
 * @param payloadKey 载荷标识
 * @param cameraType 相机类型
 * @returns GSD（厘米/像素）
 */
function getGSDByFlightHeight (height: number, payloadKey: string, cameraType?: string): number {
  const payload = PAYLOAD_WIDE_IR[payloadKey]
  if (!payload) return 0
  let sensorSize, focalLength
  if (cameraType === cameraTypes.IR) {
    sensorSize = payload.irS * 1e-4
    focalLength = payload.irF * 0.1
  } else {
    sensorSize = payload.wideS * 1e-4
    focalLength = payload.wideF * 0.1
  }
  if (sensorSize && focalLength) {
    const d = sensorSize * height * 100 / focalLength
    return Number(d.toFixed(1))
  } else {
    return 0
  }
}

// ---------- 状态更新逻辑（依赖 store）----------
const getDefaultSettings = () => currentStripRouteSettingData
const getEditInfo = () => store.state.editInfo

/**
 * 根据外扩距离重新计算并更新最小 GSD、最小高度等限制值
 */
export function setExpandDis (): void {
  const settings = getDefaultSettings()
  const payloadType = getEditInfo().payload_types[0]
  if (!payloadType) return

  const { GSD, irGSD } = calculateGSDByExpandDistance(Number(settings.allExpandDis), payloadType)

  // 更新最小限制
  settings.minGSD = GSD
  settings.minIrGSD = irGSD
  settings.minGlobalHeight = getFlightHeightByGSD(GSD, payloadType, cameraTypes.WIDE)

  // 如果包含红外相机，则使用红外 GSD 重新计算
  if (settings.imageFormat.includes(cameraTypes.IR)) {
    const finalIrGSD = getGSDByFlightHeight(settings.minGlobalHeight, payloadType, cameraTypes.IR)
    settings.minGSD = finalIrGSD
    settings.minGlobalHeight = getFlightHeightByGSD(finalIrGSD, payloadType, cameraTypes.WIDE)
  }
}

/**
 * 根据 GSD、红外 GSD 或高度中的某一项，同步另外两项
 * @param source 基准类型：'GSD' | 'irGSD' | 'height'
 */
export function syncHeightAndGSD (source: 'GSD' | 'irGSD' | 'height'): void {
  const settings = getDefaultSettings()
  const payloadType = getEditInfo().payload_types[0]
  if (!payloadType) return
  if (source === 'GSD') {
    settings.globalHeight = getFlightHeightByGSD(settings.GSD, payloadType, CAMERA_TYPES.Wide)
    settings.irGSD = getGSDByFlightHeight(settings.globalHeight, payloadType, CAMERA_TYPES.Ir)
  } else if (source === 'irGSD') {
    settings.globalHeight = getFlightHeightByGSD(settings.irGSD, payloadType, CAMERA_TYPES.Ir)
    settings.GSD = getGSDByFlightHeight(settings.globalHeight, payloadType, CAMERA_TYPES.Wide)
  } else {
    settings.GSD = getGSDByFlightHeight(settings.globalHeight, payloadType, CAMERA_TYPES.Wide)
    settings.irGSD = getGSDByFlightHeight(settings.globalHeight, payloadType, CAMERA_TYPES.Ir)
  }
}

/**
 * 显示最小限制警告
 */
function showMinLimitWarning (): void {
  message.info('当前外扩距离下，GSD/航高已达最小值！')
}

/**
 * 验证并修正 GSD / 红外 GSD / 高度，确保不低于最小值
 * @param type 要验证的类型
 */
export function validateMinLimit (type: 'GSD' | 'irGSD' | 'height'): void {
  const settings = getDefaultSettings()
  let needSync = false

  if (type === 'GSD' && settings.GSD < settings.minGSD) {
    settings.GSD = settings.minGSD
    needSync = true
  } else if (type === 'irGSD' && settings.irGSD < settings.minIrGSD) {
    settings.irGSD = settings.minIrGSD
    needSync = true
  } else if (type === 'height' && settings.globalHeight < settings.minGlobalHeight) {
    settings.globalHeight = settings.minGlobalHeight
    needSync = true
  }

  if (needSync) {
    syncHeightAndGSD(type)
    showMinLimitWarning()
  }
}

/**
 * 根据当前是否包含红外相机，校验相应的 GSD 限制
 */
export function setImageFormat (): void {
  const settings = getDefaultSettings()
  const hasIr = settings.imageFormat.includes(CAMERA_TYPES.Ir)
  validateMinLimit(hasIr ? 'irGSD' : 'GSD')
}

/**
 * 获取当前航线点并触发后续处理
 * @param points 航线点数组
 */
export function getNowPoint (points: any[]): void {
  stripPoints.value = points

  // 转换坐标格式
  const geographicPoints = points.map(point => [Number(point[0]), Number(point[1])])

  const settings = getDefaultSettings()
  const isBowRoute = settings.stripRouteType === ROUTE_TYPE.BOW_ROUTE
  const leftExpand = isBowRoute ? settings.leftExpandDis : settings.allExpandDis
  const rightExpand = isBowRoute ? settings.rightExpandDis : settings.allExpandDis
  const altitudeType = WAYLINE_MAP_TYPE_NUM[settings.heightMode]
  const routeParams = {
    geographicPoints,
    leftExpand,
    rightExpand,
    gsd: settings.GSD,
    altitudeType,
    sideOverlap: settings.orthoLidarOverlapW,
    droneType: getEditInfo().payload_types[0],
    routeType: settings.stripRouteType,
  }

  console.log(routeParams)

  getWaylineStrip(routeParams).then(res => {
    stripArea.value = res.data[0].geographicPoints
    stripAllRoutes.value = res.data[1].geographicPoints
    state.drawRouteTool?.setStripArea(stripArea.value)
    state.drawRouteTool?.setStripLine(stripAllRoutes.value)
    const area = stripArea.value.map(el => {
      return { longitude: el[0], latitude: el[1], height: 0 }
    })
    const line = stripAllRoutes.value.map(el => {
      return { longitude: el[0], latitude: el[1], height: el[2] }
    })
    const center = stripPoints.value.map(el => {
      return { longitude: el[0], latitude: el[1], height: 0 }
    })
    settings.area = calculateCesiumPolygonArea(area)
    settings.centerLineDistance = calculateCesiumPolylineLength(center)
    settings.routeDistance = calculateCesiumPolylineLength(line)
    settings.duration = settings.routeDistance / settings.autoFlightSpeed
    isSave.value = true
  })
}

/**
 * 根据外扩距离和负载类型获取最小允许的 GSD（示例算法，实际可能需后端接口）
 */
export function getMinGSDByExpand (expandDis, payloadType) {
  // 这里仅为示例逻辑，实际应根据专业公式或后端接口
  let baseGSD = 1.0
  if (expandDis < 50) baseGSD = 0.8
  else if (expandDis < 100) baseGSD = 1.2
  else baseGSD = 1.8

  if (payloadType === cameraTypes.IR) {
    return { GSD: baseGSD * 1.5, irGSD: baseGSD }
  } else {
    return { GSD: baseGSD, irGSD: baseGSD * 1.5 }
  }
}

/**
 * 根据 GSD 计算航高
 */
export function calcHeightByGSD (gsd, payloadType, lensType) {
  // 需要根据相机参数计算，此处返回模拟值
  return gsd * 100 // 示例：GSD 1cm/pixel 对应 100m 高度
}

/**
 * 根据航高计算 GSD
 */
export function calcGSDByHeight (height, payloadType, lensType) {
  return height / 100
}

/** 设置文件属性 */
export function setStripSettingData (data) {
  currentStripRouteSettingData.imageFormat = data.Document.Folder[0].payloadParam.imageFormat.split(',')
  currentStripRouteSettingData.shootType = data.Document.Folder[0].Placemark[0].shootType
  currentStripRouteSettingData.autoFlightSpeed = data.Document.Folder[0].autoFlightSpeed
  currentStripRouteSettingData.heightMode = data.Document.Folder[0].waylineCoordinateSysParam.heightMode
  currentStripRouteSettingData.globalHeight = data.Document.Folder[0].waylineCoordinateSysParam.globalShootHeight
  currentStripRouteSettingData.orthoLidarOverlapW = data.Document.Folder[0].Placemark[0].overlap.orthoCameraOverlapH
  currentStripRouteSettingData.finishAction = data.Document.missionConfig.finishAction
  currentStripRouteSettingData.flyToWaylineMode = data.Document.missionConfig.flyToWaylineMode
  currentStripRouteSettingData.leftExpandDis = currentStripRouteSettingData.allExpandDis = data.Document.Folder[0].Placemark[0].leftExtend
  currentStripRouteSettingData.rightExpandDis = data.Document.Folder[0].Placemark[0].rightExtend
  currentStripRouteSettingData.stripRouteType = data.Document.Folder[0].Placemark[0].singleLineEnable ? ROUTE_TYPE.ONE_ROUTE : ROUTE_TYPE.BOW_ROUTE
  currentStripRouteSettingData.duration = data.Document.Folder[0].duration || 0
  currentStripRouteSettingData.centerLineDistance = data.Document.Folder[0].Placemark[0].distance || 0
  if (currentStripRouteSettingData.stripRouteType === ROUTE_TYPE.ONE_ROUTE)setExpandDis()
}

/** 获取航线长度 */
export const getPolylinePointLength = (list:Cartesian3[]) => {
  let length = 0
  for (let i = 1; i < list.length; i++) {
    const point1 = list[i - 1]
    const point2 = list[i]
    length += Cartesian3.distance(point1, point2)
  }
  return length
}

/**
 * 将原始航点数组转换为带航向角、动作组等属性的标准航点对象数组
 * @param {Array<Array<number>>} waypoints - 原始航点数组，每个元素为 [lng, lat, height?]
 * @param {Object} flightOptions - 飞行选项
 * @param {number} flightOptions.autoFlightSpeed - 自动飞行速度
 * @param {*} flightOptions.payloadVal - 载荷相关参数（传递给 V 函数）
 * @returns {Array<Object>} 标准化后的航点对象数组
 */
export function setStrip (waypoints, flightOptions) {
  if (!Array.isArray(waypoints) || waypoints.length === 0) {
    return []
  }

  const result = []
  const pointsCount = waypoints.length
  const { autoFlightSpeed, payloadVal } = flightOptions

  for (let i = 0; i < pointsCount; i++) {
    const current = waypoints[i]
    const [lng, lat, altitude = 0] = current // 解构，高度默认为0

    // 计算航向角（当前点到下一点的方向，最后一个点无方向）
    let headingAngle = 0

    if (i !== pointsCount - 1) {
      const next = waypoints[i + 1]
      const currentCartesian = Cartesian3.fromDegrees(lng, lat, 0)
      const nextCartesian = Cartesian3.fromDegrees(next[0], next[1], 0)
      const angleInfo = getPointPosition(currentCartesian, nextCartesian) // 假设 I 返回 { h: 弧度值 }
      headingAngle = angleInfo.h * 180 / Math.PI
    }

    // 构建单个航点对象
    const waypoint = {
      Point: new GetNewPoint(lng, lat),
      wpml_index: i,
      wpml_executeHeight: altitude,
      wpml_waypointSpeed: autoFlightSpeed,
      wpml_waypointHeadingParam: {
        wpml_waypointHeadingMode: FollowType.FOLLOW_WAYLINE,
        wpml_waypointHeadingAngle: headingAngle,
        wpml_waypointPoiPoint: new GetNewPoint(),
        wpml_waypointHeadingAngleEnable: 1,
        wpml_waypointHeadingPathMode: FollowBadType.FOLLOW_BAD_ARC,
        wpml_waypointHeadingPoiIndex: 0,
      },
      wpml_waypointTurnParam: {
        wpml_waypointTurnMode: WaylineSmoothType.TO_POINT_AND_STOP_WITH_DISCONTINUITY_CURVATURE,
        wpml_waypointTurnDampingDist: 0,
      },
      wpml_useStraightLine: 1,
      wpml_waypointGimbalHeadingParam: {
        wpml_waypointGimbalPitchAngle: 0,
        wpml_waypointGimbalYawAngle: 0,
      },
      wpml_isRisky: BooleanEnum.FALSE,
      wpml_waypointWorkType: BooleanEnum.FALSE,
    }

    // 添加动作组（仅首尾点）
    const actionGroups = getPointWpml(payloadVal, flightOptions, pointsCount, i)
    if (i === 0) {
      waypoint.wpml_actionGroup = actionGroups.firstActionGroup
    } else if (i === pointsCount - 1) {
      waypoint.wpml_actionGroup = actionGroups.lastActionGroup
    }

    result.push(waypoint)
  }

  return result
}

/**
 * 生成航线模板信息（templateInfo）和实际航线信息（waylinesInfo）
 * @param {Object} routeConfig - 航线配置（包含所有地理、飞行参数）
 * @param {Object} payloadConfig - 载荷配置（包含 drone 和 payload 的枚举值）
 * @returns {Object} { templateInfo, waylinesInfo }
 */
// eslint-disable-next-line no-undef
export function createSaveKmlStrip (routeConfig:any, payloadConfig:any) {
  const currentTimestamp = Date.now()

  // ----- 提取配置（带默认值，增强健壮性）-----
  const {
    stripLinePoint = [], // 条带航线点（用于生成 Placemark）
    stripAreaPoint = [], // 条带区域点
    stripRouteType, // 航线类型：弓字或普通
    leftExpandDis = 0,
    rightExpandDis = 0,
    allExpandDis = 0,
    flyToWaylineMode,
    finishAction,
    takeOffSecurityHeight,
    startLat,
    startLng,
    startHeight,
    droneEnumValue,
    payloadEnumValue,
    author = '',
    createTime = Date.now(),
    templateType,
    heightMode,
    globalHeight,
    autoFlightSpeed,
    shootType,
    orthoLidarOverlapW = 0,
    imageFormat = [],
    routeDistance = 0,
    duration = 0,
  } = routeConfig

  // 扩展距离计算
  const leftExtend = stripRouteType === ROUTE_TYPE.BOW_ROUTE ? leftExpandDis : allExpandDis
  const rightExtend = stripRouteType === ROUTE_TYPE.BOW_ROUTE ? rightExpandDis : allExpandDis
  const singleLineEnable = stripRouteType === ROUTE_TYPE.BOW_ROUTE ? 0 : 1

  // ----- 公共任务配置（missionConfigBase）-----
  const missionConfigBase = {
    wpml_flyToWaylineMode: flyToWaylineMode,
    wpml_finishAction: finishAction,
    wpml_exitOnRCLost: BtnCallBackLoading.GO_CONTINUE,
    wpml_executeRCLostAction: BtnLoading.GO_BACK,
    wpml_takeOffSecurityHeight: takeOffSecurityHeight,
    wpml_takeOffRefPoint: new GetNewPoint(startLat, startLng, startHeight),
    wpml_takeOffRefPointAGLHeight: 0,
    wpml_globalTransitionalSpeed: 15,
    wpml_globalRTHHeight: 100,
    wpml_droneInfo: {
      wpml_droneEnumValue: droneEnumValue,
      wpml_droneSubEnumValue: DroneSubEnumValue[routeConfig.payloadEnumValue],
    },
    wpml_waylineAvoidLimitAreaMode: 0,
    wpml_payloadInfo: {
      wpml_payloadEnumValue: payloadEnumValue,
      wpml_payloadSubEnumValue: PayloadSubEnumValue2[routeConfig.payloadEnumValue],
      wpml_payloadPositionIndex: 0,
    },
  }

  // ----- 模板信息（templateInfo）-----
  const templateInfo = {
    wpml_author: author,
    wpml_createTime: createTime,
    wpml_updateTime: currentTimestamp,
    wpml_missionConfig: { ...missionConfigBase },
    Folder: [
      {
        wpml_templateType: templateType,
        wpml_templateId: 0,
        wpml_waylineCoordinateSysParam: {
          wpml_coordinateMode: CoordinateMode.WGS84,
          wpml_heightMode: heightMode,
          wpml_globalShootHeight: globalHeight,
        },
        wpml_autoFlightSpeed: autoFlightSpeed,
        Placemark: [
          {
            wpml_caliFlightEnable: 0,
            wpml_elevationOptimizeEnable: 1,
            wpml_shootType: shootType,
            wpml_direction: 0,
            wpml_singleLineEnable: singleLineEnable,
            wpml_cuttingDistance: 10000,
            wpml_boundaryOptimEnable: 0,
            wpml_leftExtend: leftExtend,
            wpml_rightExtend: rightExtend,
            wpml_includeCenterEnable: 0,
            wpml_efficiencyFlightModeEnable: 0,
            wpml_overlap: {
              wpml_orthoCameraOverlapH: orthoLidarOverlapW,
              wpml_orthoCameraOverlapW: orthoLidarOverlapW,
            },
            LineString: {
              coordinates: stripLinePoint.join('\n'),
            },
            wpml_ellipsoidHeight: globalHeight,
            wpml_height: globalHeight,
            wpml_stripMergedRegions: [],
            wpml_stripUseTemplateAltitude: 0,
            StripRegionsPoint: {
              coordinates: stripAreaPoint.join('\n'),
            },
          },
        ],
        wpml_payloadParam: {
          wpml_payloadPositionIndex: 0,
          wpml_focusMode: FOCUS_MODE_TYPE.FIRST_POINT,
          wpml_meteringMode: METERING_MODE_TYPE.AVERAGE,
          wpml_returnMode: RETURN_MODE_TYPE.SINGLE_RETURN_FIRST,
          wpml_samplingRate: 240000,
          wpml_scanningMode: SCANNING_MODE_TYPE.REPETITIVE,
          wpml_imageFormat: imageFormat.join(','), // 保持与原始行为一致（join 默认逗号）
        },
      },
    ],
  }

  // ----- 起始动作组 -----
  const startActionGroup = createStartActions(payloadEnumValue)

  // ----- 实际航线信息（waylinesInfo）-----
  // 注意：此处调用 setStrip 生成 Placemark 数组，解决了原始代码中参数混淆的问题
  const processedWaypoints = setStrip(stripLinePoint, {
    autoFlightSpeed,
    payloadVal: payloadConfig, // 根据 V 函数的实际需求传递 payloadVal
    ...routeConfig
  })

  const waylinesInfo = {
    wpml_missionConfig: { ...missionConfigBase },
    Folder: [
      {
        wpml_templateId: 0,
        wpml_executeHeightMode: CoordinateMode.WGS84,
        wpml_waylineId: 0,
        wpml_distance: routeDistance,
        wpml_duration: duration,
        wpml_autoFlightSpeed: autoFlightSpeed,
        wpml_startActionGroup: startActionGroup,
        Placemark: processedWaypoints,
      },
    ],
  }

  return { templateInfo, waylinesInfo }
}
